import React from 'react';
import { GameTheme } from './games';

// ── STREAK BAR ───────────────────────────────────────────────────────────────
//
// A slim progress bar that lives inline in the top toolbar. It charges as the
// player builds a correct-answer streak; each correct answer fills it a little
// more, a wrong answer drains it (the parent resets the streak). When it fills,
// it morphs into a glowing "▶ Play!" button. Earned games bank up — a small
// 🎮 xN count rides on the bar when more than one is waiting.

interface StreakBarProps {
  theme: GameTheme;
  charge: number; // correct answers toward the next token (0..interval)
  interval: number; // answers needed to earn one token
  tokens: number; // banked game-plays
  isKid: boolean;
  onPlay: () => void;
}

export default function StreakBar({ theme, charge, interval, tokens, isKid, onPlay }: StreakBarProps) {
  const ready = tokens > 0;
  const pct = ready ? 100 : Math.min((charge / interval) * 100, 100);

  return (
    <div style={{ flex: 1, minWidth: '140px', display: 'flex' }}>
      <style>{`
        @keyframes streakbar-glow {
          0%, 100% { box-shadow: 0 0 6px 1px ${theme.accent}55; }
          50% { box-shadow: 0 0 14px 3px ${theme.accent}cc; }
        }
        @keyframes streakbar-shine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(260%); }
        }
      `}</style>

      <button
        onClick={ready ? onPlay : undefined}
        disabled={!ready}
        aria-label={ready
          ? `Play a game — ${tokens} available`
          : `Streak to a game: ${charge} of ${interval}`}
        style={{
          position: 'relative',
          flex: 1,
          height: isKid ? '32px' : '30px',
          borderRadius: '16px',
          border: `1.5px solid ${ready ? theme.accent : theme.light}`,
          background: theme.card,
          padding: 0,
          overflow: 'hidden',
          cursor: ready ? 'pointer' : 'default',
          animation: ready ? 'streakbar-glow 1.4s ease-in-out infinite' : 'none',
          WebkitTapHighlightColor: 'transparent',
          display: 'block',
        }}
      >
        {/* fill */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${theme.btn}, ${theme.accent})`,
            borderRadius: '14px',
            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />

        {/* moving shine when ready */}
        {ready && (
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '40%',
            background: 'linear-gradient(90deg, transparent, #ffffff66, transparent)',
            animation: 'streakbar-shine 1.6s ease-in-out infinite',
          }} />
        )}

        {/* label (sits above fill) */}
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontWeight: 700,
            fontSize: isKid ? '0.92rem' : '0.82rem',
            color: ready ? '#fff' : theme.text,
            textShadow: ready ? '0 1px 2px rgba(0,0,0,0.35)' : 'none',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {ready ? (
            <>▶ Play!{tokens > 1 && <span style={{
              background: '#ef4444',
              borderRadius: '9px',
              padding: '0 6px',
              fontSize: '0.72rem',
              border: '1.5px solid #fff',
            }}>🎮 {tokens}</span>}</>
          ) : (
            <>🔥 {charge}/{interval} to a game</>
          )}
        </span>
      </button>
    </div>
  );
}
